import { Switch } from "@/components/ui/switch";
import { useEffect, useState } from "react";

interface AsyncToggleProps {
  initialState: boolean;
  enableFunction: {
    isDone: boolean;
    call: (...args: any[]) => Promise<void>;
    isFailed: boolean;
  };
  disableFunction: {
    isDone: boolean;
    call: (...args: any[]) => Promise<void>;
    isFailed: boolean;
  };
  disabled?: boolean;
  onCheckedChange?: (checked: boolean) => void;
}

export function AsyncToggle({
  initialState,
  enableFunction,
  disableFunction,
  disabled = false,
  onCheckedChange,
}: AsyncToggleProps) {
  const [isChecked, setIsChecked] = useState(initialState);
  const [pendingToggle, setPendingToggle] = useState<{
    targetState: boolean;
  }>();

  // Update internal state when initialState changes
  useEffect(() => {
    setIsChecked(initialState);
  }, [initialState]);

  // Handle successful enable
  useEffect(() => {
    if (enableFunction.isDone && pendingToggle?.targetState === true) {
      setIsChecked(true);
      setPendingToggle(undefined);
      onCheckedChange?.(true);
    }
  }, [enableFunction.isDone, pendingToggle, onCheckedChange]);

  // Handle successful disable
  useEffect(() => {
    if (disableFunction.isDone && pendingToggle?.targetState === false) {
      setIsChecked(false);
      setPendingToggle(undefined);
      onCheckedChange?.(false);
    }
  }, [disableFunction.isDone, pendingToggle, onCheckedChange]);

  // Handle failed enable - revert to original state
  useEffect(() => {
    if (enableFunction.isFailed && pendingToggle?.targetState === true) {
      setIsChecked(false);
      setPendingToggle(undefined);
    }
  }, [enableFunction.isFailed, pendingToggle]);

  // Handle failed disable - revert to original state
  useEffect(() => {
    if (disableFunction.isFailed && pendingToggle?.targetState === false) {
      setIsChecked(true);
      setPendingToggle(undefined);
    }
  }, [disableFunction.isFailed, pendingToggle]);

  const handleToggle = async () => {
    // Don't allow multiple operations
    if (pendingToggle) {
      return;
    }

    const targetState = !isChecked;

    // Set pending toggle for tracking
    setPendingToggle({ targetState });

    // Optimistically update UI
    setIsChecked(targetState);

    // Call the appropriate function
    if (targetState) {
      await enableFunction.call();
    } else {
      await disableFunction.call();
    }
  };

  return (
    <Switch
      checked={isChecked}
      onCheckedChange={handleToggle}
      disabled={disabled || !!pendingToggle}
    />
  );
}
